import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import {
  GridColumnMenuFilter,
  GridColumnMenuFilterUI,
  GridColumnMenuFilterUIProps,
  GridColumnMenuProps,
} from '@progress/kendo-react-grid';
import { NumericTextBox, NumericTextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { useMemo } from 'react';

export interface CustomFilterCellProps extends GridColumnMenuProps {
  filterFormat?: string;
  step?: number;
}
export interface CustomNumericFilterUI extends GridColumnMenuFilterUIProps {
  filterFormat?: string;
  step?: number;
}

const NumericFilterUI = (filterUIProps: CustomNumericFilterUI) => {
  const { value, operators, operator = '', onChange, filterType } = filterUIProps.firstFilterProps;
  const { filterFormat = 'n2', step = 0.1 } = filterUIProps;
  const dropDownValue = useMemo(
    () => operators.find((item) => item.operator === operator),
    [operator, operators]
  );

  const IsUnaryFilter = (operator: string) => operator === 'isnull' || operator === 'isnotnull';
  const { isPercentageFormat, numericValue } = useMemo(() => {
    const isPercentageFormat = filterFormat.includes('p') || filterFormat.includes('%');
    const numericValue = value && isPercentageFormat ? Number(value) / 100 : value;
    return {
      isPercentageFormat,
      numericValue,
    };
  }, [filterFormat, value]);

  const handleTextChange = (e: NumericTextBoxChangeEvent) => {
    const defaultOperator = operators[0].operator;
    let currentOperator = operator as string;
    let currentValue = e.value;
    if (value === null && IsUnaryFilter(currentOperator)) {
      currentOperator = defaultOperator;
    }
    if (isPercentageFormat) {
      currentValue = Number(currentValue) * 100;
    }
    onChange({
      value: currentValue,
      operator: currentOperator,
      syntheticEvent: e.syntheticEvent,
    });
  };

  const onDropDownChange = (e: DropDownListChangeEvent) => {
    let currentValue = value;

    if (IsUnaryFilter(e.value.operator)) {
      currentValue = null;
    }
    if (value === null && !IsUnaryFilter(e.value.operator)) {
      currentValue = undefined;
    }
    onChange({
      value: currentValue,
      operator: e.value.operator,
      syntheticEvent: e.syntheticEvent,
    });
  };

  return (
    <>
      {filterType === 'numeric' ? (
        <>
          <DropDownList
            data={operators}
            value={dropDownValue}
            textField="text"
            onChange={onDropDownChange}
          />
          <NumericTextBox
            value={numericValue}
            format={filterFormat}
            onChange={handleTextChange}
            step={step}
          />
        </>
      ) : (
        <GridColumnMenuFilterUI {...filterUIProps} />
      )}
    </>
  );
};

const CustomFilterCell = (columnMenuProps: CustomFilterCellProps) => (
  <GridColumnMenuFilter
    {...columnMenuProps}
    expanded={true}
    hideSecondFilter
    filterUI={(filterUIProps) => (
      <NumericFilterUI
        {...filterUIProps}
        step={columnMenuProps.step}
        filterFormat={columnMenuProps.filterFormat}
      />
    )}
  />
);

export default CustomFilterCell;
